import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * `className` porte la largeur (et la visibilité responsive) de la colonne : elle est
 * appliquée à l'en-tête ET à chaque cellule, ce qui garantit leur alignement.
 */
export type DataTableColumn<K extends string> = {
  key: K;
  header: ReactNode;
  className: string;
  sortable?: boolean;
};

export type DataTableSort<K extends string> = {
  key: K;
  direction: "asc" | "desc";
};

/** Clic sur un en-tête : croissant, puis décroissant, puis retour à l'ordre d'origine. */
export const nextSort = <K extends string>(
  sort: DataTableSort<K> | undefined,
  key: K
): DataTableSort<K> | undefined =>
  sort?.key !== key
    ? { key, direction: "asc" }
    : sort.direction === "asc"
      ? { key, direction: "desc" }
      : undefined;

export const compareText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

const LINE = "flex justify-between items-center gap-2 px-4";

// Sous le tableau : la pagination et le padding bas de la page.
const SPACE_BELOW = 72;
const MIN_HEIGHT = 240;

/**
 * Hauteur disponible entre le haut du tableau et le bas de l'écran. Mesurée à chaque
 * rendu : ce qui précède le tableau (puces de filtres, barre de filtres qui passe à la
 * ligne) change de hauteur avec l'état du parent, qui nous fait alors re-rendre.
 */
const useRemainingHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(MIN_HEIGHT);

  useLayoutEffect(() => {
    const measure = () => {
      const top = ref.current?.getBoundingClientRect().top ?? 0;
      setHeight(Math.max(MIN_HEIGHT, window.innerHeight - top - SPACE_BELOW));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });

  return { ref, height };
};

const SORT_ICON = { asc: "sort-up", desc: "sort-down" } as const;
const ARIA_SORT = { asc: "ascending", desc: "descending" } as const;

type DataTableProps<K extends string> = {
  columns: DataTableColumn<K>[];
  /** Change à chaque nouvelle page ou nouveau filtre : la zone remonte alors en haut. */
  scrollKey: string;
  sort?: DataTableSort<K>;
  onSort?: (key: K) => void;
  children: ReactNode;
};

// L'en-tête reste hors de la zone de défilement ; `table-scroll` lui réserve la même
// gouttière qu'au corps, sans quoi la barre décalerait les cellules sous les titres.
export const DataTable = <K extends string>({
  columns,
  scrollKey,
  sort,
  onSort,
  children,
}: DataTableProps<K>) => {
  const { ref, height } = useRemainingHeight();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [scrollKey]);

  return (
    <div
      ref={ref}
      role="table"
      className="flex flex-col bg-grey-light shadow p-1 rounded-lg"
      style={{ maxHeight: height }}
    >
      <div
        role="row"
        className={`table-scroll shrink-0 overflow-hidden py-2.5 ${LINE}`}
      >
        {columns.map((column) => {
          const direction =
            sort?.key === column.key ? sort.direction : undefined;
          return (
            <div
              key={column.key}
              role="columnheader"
              aria-sort={direction ? ARIA_SORT[direction] : undefined}
              className={`font-oxaniumMedium uppercase ${column.className}`}
            >
              {column.sortable && onSort ? (
                <button
                  type="button"
                  onClick={() => onSort(column.key)}
                  className="flex items-center gap-1.5 uppercase hover:text-secondary transition-colors"
                >
                  {column.header}
                  <FontAwesomeIcon
                    icon={direction ? SORT_ICON[direction] : "sort"}
                    className={`h-3 w-3 ${direction ? "" : "text-grey-medium"}`}
                  />
                </button>
              ) : (
                column.header
              )}
            </div>
          );
        })}
      </div>
      <div
        ref={scrollRef}
        role="rowgroup"
        className="table-scroll min-h-0 overflow-y-auto"
      >
        {children}
      </div>
    </div>
  );
};

type DataTableRowProps<K extends string> = {
  columns: DataTableColumn<K>[];
  cells: Record<K, ReactNode>;
  onClick?: () => void;
  selected?: boolean;
};

export const DataTableRow = <K extends string>({
  columns,
  cells,
  onClick,
  selected = false,
}: DataTableRowProps<K>) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Entrée sur l'étoile ou l'œil remonte jusqu'ici : seule la ligne elle-même s'ouvre.
    if (!onClick || e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="row"
      aria-current={selected}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={`transition-colors duration-200 border-b border-grey-light ${
        selected
          ? "bg-secondary-text shadow-[inset_4px_0_0_theme(colors.secondary)]"
          : "bg-white"
      } ${
        onClick
          ? "cursor-pointer hover:bg-secondary-text focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-secondary"
          : ""
      }`}
    >
      <div className={`py-4 my-1 ${LINE}`}>
        {columns.map((column) => (
          <div key={column.key} role="cell" className={column.className}>
            {cells[column.key]}
          </div>
        ))}
      </div>
    </div>
  );
};

/** Lignes fantômes pendant le chargement : la mise en page ne saute pas à l'arrivée. */
export const DataTableSkeletonRows = <K extends string>({
  columns,
  count = 8,
}: {
  columns: DataTableColumn<K>[];
  count?: number;
}) => (
  <div aria-busy="true">
    {Array.from({ length: count }, (_, index) => (
      <div
        key={index}
        role="row"
        className="bg-white border-b border-grey-light"
      >
        <div className={`py-4 my-1 ${LINE}`}>
          {columns.map((column) => (
            <div key={column.key} role="cell" className={column.className}>
              <div className="h-4 w-3/4 rounded bg-grey-light animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);
